import { FC, PropsWithChildren, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { ConfigProvider, Flex } from 'antd';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './UserViewPermissionsFormTable.translations.json';
import {
  CloneFormTableRowButton,
  ConfidentialDataObjectFormSelect,
  CreateFormTableRowButton,
  defaultUserViewPermissionFormData,
  DeleteFormTableRowButton,
  FormDateRangePicker,
  FormField,
  FormSelect,
  getConfidentialDataObjectLabel,
  getDatePeriodString,
  getFormTableFieldName,
  getIsoDateToday,
  getSortViewPermissionsByDataObject,
  getUserViewPermissionType,
  ISODate,
  sortViewPermissionsByType,
  sortViewPermissionsByValidPeriod,
  TanstackFormTable,
  USER_VIEW_PERMISSION_DEFAULT_SORT,
  useConfidentialDataObjectsQuery,
  UserViewPermissionFormDataType,
  UserViewPermissionTypeName,
  useUserFormLabelTranslation,
} from '@atlas-frontend/sdk';
import { createColumnHelper } from '@tanstack/react-table';

const columnHelper = createColumnHelper<UserViewPermissionFormDataType>();

const { addTranslations, useTranslation } = i18nNsFactory(
  'user-view-permissions-form-table',
);
addTranslations(translations);

export const UserViewPermissionsFormTable = () => {
  const { t } = useUserFormLabelTranslation();
  const { data: confidentialDataObjects, isPending } =
    useConfidentialDataObjectsQuery();

  const columns = useMemo(
    () => [
      columnHelper.accessor('type' as any, {
        header: t('viewPermissions.type.label'),
        meta: { width: 70 },
        enableColumnFilter: false,
        cell: (props) => (
          <FormField
            name={getFormTableFieldName(props)}
            size="small"
            preview={{
              previewOnly: true,
              render: () => (
                <UserViewPermissionTargetTypePreview
                  formDataIndex={props.row.index}
                />
              ),
            }}
          />
        ),
        sortingFn: sortViewPermissionsByType,
      }),
      columnHelper.accessor('id', {
        header: t('viewPermissions.id.label'),
        enableColumnFilter: false,
        cell: (props) => {
          const name = getFormTableFieldName(props);
          return (
            <FormField
              name={name}
              size="small"
              preview={{
                previewOnly: Boolean(props.row.original.id),
                render: (value) =>
                  getConfidentialDataObjectLabel(
                    confidentialDataObjects,
                    value,
                  ) || value,
              }}
            >
              <FormSelect size="small" name={name} autoFocus defaultOpen>
                <ConfidentialDataObjectFormSelect
                  confidentialDataObjects={confidentialDataObjects}
                />
              </FormSelect>
            </FormField>
          );
        },
        sortingFn: getSortViewPermissionsByDataObject(confidentialDataObjects),
      }),
      columnHelper.accessor('validPeriod', {
        header: t('viewPermissions.validPeriod.label'),
        meta: { width: 266 },
        enableColumnFilter: false,
        cell: (props) => {
          const name = getFormTableFieldName(props);
          return (
            <FormField
              name={name}
              size="small"
              preview={{
                render: (value) => getDatePeriodString(value?.[0], value?.[1]),
              }}
            >
              <FormDateRangePicker
                size="small"
                allowEmpty={[true, true]}
                autoFocus
                name={name}
              />
            </FormField>
          );
        },
        sortingFn: sortViewPermissionsByValidPeriod,
      }),
      columnHelper.accessor('operations' as any, {
        header: () => (
          <Flex justify="center" gap="small">
            <CreateFormTableRowButton
              autoFocusField="id"
              defaultRowValues={getDefaultRowValues()}
            />
          </Flex>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        cell: (props) => (
          <Flex justify="center" gap="small">
            <CloneFormTableRowButton context={props} autoFocusField="id" />
            <DeleteFormTableRowButton context={props} />
          </Flex>
        ),
        meta: {
          width: 88,
          className: 'sticky-right',
        },
      }),
    ],
    [confidentialDataObjects, t],
  );

  if (isPending) {
    return null;
  }

  return (
    <EmptyProvider>
      <TanstackFormTable
        name="viewPermissions"
        columns={columns}
        defaultSort={USER_VIEW_PERMISSION_DEFAULT_SORT}
        sticky
        virtual
        scrollContainerSelector="#detail-layout-main-content"
      />
    </EmptyProvider>
  );
};

const getDefaultRowValues = () => ({
  ...defaultUserViewPermissionFormData,
  validPeriod: [getIsoDateToday(), null] as [ISODate | null, null],
});

const EmptyProvider: FC<PropsWithChildren> = (props) => {
  const { children } = props;
  const { t } = useTranslation();

  return (
    <ConfigProvider renderEmpty={() => t('empty-fallback')}>
      {children}
    </ConfigProvider>
  );
};

const UserViewPermissionTargetTypePreview: FC<{ formDataIndex: number }> = (
  props,
) => {
  const { formDataIndex } = props;
  const id = useWatch({
    name: `viewPermissions.${formDataIndex}.id`,
  }) as string;
  const type = getUserViewPermissionType(id);

  if (!type) {
    return null;
  }

  return <UserViewPermissionTypeName type={type} />;
};
